import { Upload } from 'lucide-react';
import { OnboardingData } from '../../../types/onboarding';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';

interface ProductInformationManualEntryProps {
  data: OnboardingData;
  handleImageUpload: () => void;
  selectMainImage: (index: number) => void;
  removeImage: (index: number) => void;
  selectedImageIndex: number;
  productData: any;
  updateProductData: (data: any) => void;
}

function ProductInformationManualEntry({
  data,
  handleImageUpload,
  selectMainImage,
  removeImage,
  selectedImageIndex,
  productData,
  updateProductData,
}: ProductInformationManualEntryProps) {
  const { language } = useAuth();
  return (
    <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-lg font-medium">{dictionary["product-information"][language]}</h4>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {data.visualContent.imagesBase64.length > 0 ? (
            <>
              <div className="aspect-square rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={data.visualContent.imagesBase64[0]}
                  alt="Main product"
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="grid grid-cols-4 gap-2">
                {data.visualContent.imagesBase64.map((image, index) => (
                  <div
                    key={index}
                    className="relative"
                  >
                    <div
                      className={`aspect-square rounded-lg overflow-hidden border cursor-pointer ${index === selectedImageIndex ? 'border-primary-600 ring-1 ring-primary-600' : 'border-gray-200'
                        }`}
                      onClick={() => selectMainImage(index)}
                    >
                      <img
                        src={image}
                        alt={`Product view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                      onClick={() => removeImage(index)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="border-2 border-dashed border-primary-200 rounded-lg bg-primary-50 p-8 flex flex-col items-center justify-center">
              <button
                type="button"
                onClick={handleImageUpload}
                className="btn btn-primary mb-4"
              >
                <Upload className="w-5 h-5 mr-2" />
                {dictionary["upload-images"][language]}
              </button>

              <p className="text-center text-gray-600">
                {dictionary["drag-and-drop-your-images-here-or-click-to-select"][language]}
              </p>
              <p className="text-center text-gray-500 text-sm mt-1">
                {dictionary["accepted-formats"][language]}
              </p>
            </div>
          )}

          {data.visualContent.images.length > 0 && (
            <button
              type="button"
              onClick={handleImageUpload}
              className="btn btn-secondary w-full"
            >
              <Upload className="w-5 h-5 mr-2" />
              Add more images
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="product-name" className="block text-sm font-medium text-gray-700 mb-1">
              {dictionary["product-name"][language]}
            </label>
            <input
              id="product-name"
              type="text"
              className="input"
              placeholder="Example: Adidas predator"
              value={productData.name}
              onChange={(e) => updateProductData({ ...productData, name: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="product-price" className="block text-sm font-medium text-gray-700 mb-1">
              {dictionary["price"][language]}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span className="text-gray-500">$</span>
              </div>
              <input
                id="product-price"
                type="number"
                className="input pl-7"
                placeholder="0.00"
                value={productData.price}
                onChange={(e) => updateProductData({ ...productData, price: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label htmlFor="product-details" className="block text-sm font-medium text-gray-700 mb-1">
              {dictionary["product-details"][language]} 
            </label>
            <textarea
              id="product-details"
              className="textarea h-32"
              placeholder="Describe the main features and benefits of your product..."
              value={productData.details}
              onChange={(e) => updateProductData({ ...productData, details: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductInformationManualEntry;
